import { sanitizeCountryCode } from "@expert/common-ui";
import { useCallIntroFlowQuestions } from "@expert/contentful";
import { useGaiaWebsocket } from "@expert/gaia";
import { useDebouncedCallback } from "@mantine/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";

import { getRootDispatcher, useAnalytics } from "@/analytics";
import { useCallDetailsStore } from "@/call-details";
import { useCustomerProgram, useVerifiedCustomerStore } from "@/customer-info";
import { useIVRInfo } from "@/customer-info/api";
import { useAutoSelectSurvey } from "@/customer-info/hooks/useAutoSelectSurvey";
import { useGetPartnerPrograms, usePartnerConfig } from "@/partner/usePartnerConfig";
import { useProductOfferStore } from "@/sales-tools";
import { isSimulatedVoiceTask, usePartner, useSessionId, useTypedMostRecentTask } from "@/sdk";

import type { CustomerVerificationViewProps } from "../CustomerVerificationView";

import { useVerificationFlowStore } from "../../customer-verification-store";
import { compareName } from "../utils/compareName";
import { computeVerificationUIConfig } from "../utils/computeVerificationUIConfig";
import { useConfirmCustomerVerification } from "./useConfirmCustomerVerification";
import { useCustomerVerificationForm } from "./useCustomerVerificationForm";
import { useGetCustomerDetails } from "./useGetCustomerDetails";
import { useGetCustomerDetailsByBAN } from "./useGetCustomerDetailsByBAN";
import { useIsConfirmationDisabled } from "./useIsConfirmationDisabled";
import { usePreviousSessionSummary } from "./usePreviousSessionSummary";

export interface UseCustomerVerificationParams {
    mdnFromTask: string;
    isPreviousSessionSummaryEnabled: boolean;
}

/**
 * Main orchestrator hook for the customer verification flow.
 * Combines all the smaller hooks and prepares props for CustomerVerificationView.
 */
export function useCustomerVerification(params: UseCustomerVerificationParams): CustomerVerificationViewProps {
    const { mdnFromTask, isPreviousSessionSummaryEnabled } = params;

    const { task: mostRecentTask, isVoice, isChat } = useTypedMostRecentTask();
    const sessionId = useSessionId();
    const partner = usePartner();
    const { dispatcher } = useAnalytics();
    const rootDispatcher = getRootDispatcher();
    const gaiaWS = useGaiaWebsocket();

    if (!mostRecentTask || (!isVoice && !isChat)) {
        throw new Error("Active task is not a supported task type (voice or chat)");
    }

    const isExpertUniversitySimulatedVoiceTask = useMemo(() => isSimulatedVoiceTask(mostRecentTask), [mostRecentTask]);
    const {
        features: {
            programSelectionEnabled,
            verificationType,
            isInternationalPhoneEnabled = false,
            defaultCountry = "US",
        },
        additionalVerificationFields,
        blocklistedNumbers,
        blocklistedPrefixes,
        survey,
        orderIdMask,
    } = usePartnerConfig();

    const programs = useGetPartnerPrograms();
    const { customerProgram } = useCustomerProgram();
    const useCustomerAgreementProgram = survey?.useCustomerAgreementProgram ?? false;
    const sanitizedDefaultCountry = sanitizeCountryCode(defaultCountry);

    const { customer, isEditingVerificationInput } = useVerifiedCustomerStore((state) =>
        state.getVerifiedCustomer(sessionId),
    );
    const unverifiedCustomerData = useVerifiedCustomerStore((state) => state.getUnverifiedCustomerData(sessionId));
    const { setUnverifiedCustomerData, setIsEditingVerificationInput } = useVerifiedCustomerStore.getState();

    const stepHistory = useVerificationFlowStore((state) => state.getStepHistory(sessionId));
    const currentStep = stepHistory.at(stepHistory.length - 1) ?? "CustomerPhoneInput";
    const isEditingCustomerInformation = stepHistory.includes("VerificationComplete");

    const latestCustomerData = { ...customer, ...unverifiedCustomerData };
    const mdn = latestCustomerData?.mdn ?? mdnFromTask;
    const customerName = latestCustomerData?.fullName;

    const {
        intent,
        summary,
        device,
        customerName: customerNameFromIVR,
        orderNumber: orderNumberFromIVR,
        productLink,
    } = useIVRInfo();

    const products = useProductOfferStore((state) => state.getProducts(sessionId));
    const hasProducts = products.length > 0;

    const uiConfig = computeVerificationUIConfig({
        isChat,
        programSelectionEnabled,
        programs,
        useCustomerAgreementProgram,
        additionalVerificationFields,
        verificationType,
        hasProducts,
    });

    useAutoSelectSurvey();

    const [currentSubscriptions, setCurrentSubscriptions] = useState<string[] | undefined>();

    const callDetails = useCallDetailsStore.getState().callDetails;
    const callIntroQuestions = useCallIntroFlowQuestions(partner, { enabled: !isChat });

    const form = useCustomerVerificationForm({
        sessionId,
        verificationType,
        initialMdn: mdn,
        initialEmail: latestCustomerData?.email ?? "",
        initialOrderId: latestCustomerData?.orderId ?? orderNumberFromIVR ?? "",
        initialAccountNumber: latestCustomerData?.accountNumber ?? "",
        showAdditionalDetailsEmailInput: uiConfig.showAdditionalDetailsEmailInput,
        showAdditionalDetailsOrderIdInput: uiConfig.showAdditionalDetailsOrderIdInput,
        showAdditionalDetailsPhoneNumberInput: uiConfig.showAdditionalDetailsPhoneNumberInput,
        orderIdMask,
        blocklistedNumbers,
        blocklistedPrefixes,
        dispatcher,
        shouldSkipMdnValidation: isExpertUniversitySimulatedVoiceTask,
    });

    const { addToTimeline: addPrevSummaryToTimeline } = usePreviousSessionSummary({
        sessionId,
        isPreviousSessionSummaryEnabled,
        isChat,
        rootDispatcher,
    });

    const customerDetailsCallback = useCallback(
        (
            result: Parameters<typeof addPrevSummaryToTimeline>[0] extends infer T
                ? { previousSummary?: T; primaryAccountHolder?: { email?: string } }
                : never,
        ) => {
            // Only update the email input if the field is currently empty
            if (
                uiConfig.showAdditionalDetailsEmailInput &&
                form.additionalDetails.getValues().email === "" &&
                result?.primaryAccountHolder?.email != null
            ) {
                form.additionalDetails.setFieldValue("email", result.primaryAccountHolder.email);
            }

            addPrevSummaryToTimeline(result.previousSummary);
        },
        [uiConfig.showAdditionalDetailsEmailInput, form, addPrevSummaryToTimeline],
    );

    const { data: mdnLookupData, isLoading: isCustomerDetailsByMDNLoading } = useGetCustomerDetails({
        mdn,
        partner,
        dispatcher,
        sessionId,
        customerDetailsCallback,
        verificationType,
        shouldSkipMdnValidation: isExpertUniversitySimulatedVoiceTask,
    });

    const {
        data: banLookupData,
        isLoading: isCustomerDetailsByBANLoading,
        refetch: fetchBanLookupData,
    } = useGetCustomerDetailsByBAN({
        billingAccountNumber: form.verification.getValues().billingAccountNumber,
        pin: form.verification.getValues().pin,
        partner,
        dispatcher,
        customerDetailsCallback,
        mdn,
    });

    const customerData = (verificationType === "phoneNumber" ? mdnLookupData : banLookupData) ?? undefined;
    const isCustomerDetailsLoading = isCustomerDetailsByMDNLoading || isCustomerDetailsByBANLoading;

    const { isConfirmationDisabled, reason: reasonConfirmationIsDisabled } = useIsConfirmationDisabled({
        additionalDetailsFormIsValid: form.additionalDetails.isValid(),
        customerName,
        customerProgram,
        isEditingVerificationInput,
        mdn,
        sessionId,
        showProgramSelection: uiConfig.showProgramSelection,
        verificationFormIsValid: form.verification.isValid(),
        verificationType,
        shouldSkipMdnValidation: isExpertUniversitySimulatedVoiceTask,
    });

    const { confirmCustomerVerification } = useConfirmCustomerVerification();

    const debouncedSetUnverifiedCustomerData = useDebouncedCallback(
        (sid: string, customerData: Record<string, unknown>) => {
            setUnverifiedCustomerData(sid, customerData);
        },
        { delay: 300, flushOnUnmount: true },
    );

    useEffect(() => {
        if (stepHistory.includes("VerificationComplete")) {
            setIsEditingVerificationInput(sessionId, true);
            setUnverifiedCustomerData(sessionId, { fullName: undefined });
        }
    }, [sessionId, setIsEditingVerificationInput, setUnverifiedCustomerData, stepHistory]);

    const handleConfirmClick = useCallback(async () => {
        if (!form.additionalDetails.isValid() || !gaiaWS) {
            return;
        }

        const additionalDetailsValues = form.additionalDetails.getValues();

        await confirmCustomerVerification({
            sessionId,
            customerName: customerName ?? "",
            mdn,
            mdnFromTask,
            verificationType,
            customerData,
            showAdditionalDetailsEmailInput: uiConfig.showAdditionalDetailsEmailInput,
            showAdditionalDetailsOrderIdInput: uiConfig.showAdditionalDetailsOrderIdInput,
            additionalDetailsEmail: additionalDetailsValues.email,
            additionalDetailsOrderId: additionalDetailsValues.orderId,
            additionalDetailsMdn: additionalDetailsValues.mdn,
            billingAccountNumber: form.verification.getValues().billingAccountNumber,
            currentSubscriptions,
            flushDebouncedUpdates: form.flushDebouncedUpdates,
        });
    }, [
        form,
        gaiaWS,
        sessionId,
        customerName,
        mdn,
        mdnFromTask,
        verificationType,
        customerData,
        uiConfig,
        currentSubscriptions,
        confirmCustomerVerification,
    ]);

    const handleMdnComplete = useCallback(
        (input: string) => {
            setUnverifiedCustomerData(sessionId, {
                mdn: input,
                ...(input !== mdn ? { fullName: undefined } : {}),
            });
            setIsEditingVerificationInput(sessionId, false);
        },
        [sessionId, mdn, setUnverifiedCustomerData, setIsEditingVerificationInput],
    );

    const handleMdnEditClick = useCallback(() => {
        setIsEditingVerificationInput(sessionId, true);
    }, [sessionId, setIsEditingVerificationInput]);

    const handleNameSelect = useCallback(
        (input: string | undefined) => {
            debouncedSetUnverifiedCustomerData(sessionId, { fullName: input });
        },
        [sessionId, debouncedSetUnverifiedCustomerData],
    );

    const handleBanSave = useCallback(() => {
        const didBanOrPinChange = form.verification.isDirty("billingAccountNumber") || form.verification.isDirty("pin");
        setUnverifiedCustomerData(sessionId, {
            accountNumber: form.verification.getValues().billingAccountNumber,
            ...(didBanOrPinChange ? { fullName: undefined } : {}),
        });
        setIsEditingVerificationInput(sessionId, false);
        void dispatcher.dispatchBusinessEvent("BAN_Submitted");
        void fetchBanLookupData();
    }, [form, sessionId, setUnverifiedCustomerData, setIsEditingVerificationInput, dispatcher, fetchBanLookupData]);

    const handleBanEditClick = useCallback(() => {
        setIsEditingVerificationInput(sessionId, true);
    }, [sessionId, setIsEditingVerificationInput]);

    const handleSubscriptionsChange = useCallback((subscriptions: string[] | undefined) => {
        setCurrentSubscriptions(subscriptions);
    }, []);

    return {
        sessionId,
        currentStep,

        config: {
            verificationType,
            isInternationalPhoneEnabled,
            defaultCountry: sanitizedDefaultCountry,
            orderIdMask,
            dispatcher,
            blocklistedNumbers,
            blocklistedPrefixes,
            isExpertUniversitySimulatedVoiceTask,
        },

        customer: {
            data: customerData,
            mdn,
            isLoading: isCustomerDetailsLoading,
            nameFromIVR: customerNameFromIVR,
        },

        editing: {
            isEditingVerificationInput,
            isEditingCustomerInformation,
        },

        visibility: {
            showProgramSelection: uiConfig.showProgramSelection,
            showAdditionalDetailsEmailInput: uiConfig.showAdditionalDetailsEmailInput,
            showAdditionalDetailsOrderIdInput: uiConfig.showAdditionalDetailsOrderIdInput,
            showAdditionalDetailsPhoneNumberInput: uiConfig.showAdditionalDetailsPhoneNumberInput,
        },

        ivr: {
            intent,
            summary,
            device,
            productLink,
        },

        verificationForm: {
            getInputProps: form.verification.getInputProps,
            isValid: form.verification.isValid,
            isDirty: form.verification.isDirty,
            getValues: form.verification.getValues,
            setFieldValue: form.verification.setFieldValue,
            key: form.verification.key,
        },

        additionalDetailsForm: {
            getInputProps: form.additionalDetails.getInputProps,
            setFieldValue: form.additionalDetails.setFieldValue,
            key: form.additionalDetails.key,
        },

        confirmation: {
            isDisabled: isConfirmationDisabled,
            disabledReason: reasonConfirmationIsDisabled,
            compareName: () => compareName(customerData, customerName),
        },

        handlers: {
            onConfirmClick: handleConfirmClick,
            onMdnComplete: handleMdnComplete,
            onMdnEditClick: handleMdnEditClick,
            onNameSelect: handleNameSelect,
            onBanSave: handleBanSave,
            onBanEditClick: handleBanEditClick,
            onSubscriptionsChange: handleSubscriptionsChange,
        },

        callIntro: {
            partner,
            callDetails,
            questions: callIntroQuestions,
        },

        programs,
    };
}
